// Fire-and-forget activity pings for the inspection report form.
//
// Volume is deliberately bounded. The text draft saves 400ms after every keystroke
// pause, so pinging per save would mean hundreds of requests per session from a
// technician's phone on cell data. Instead:
//   - `opened`          once per mount
//   - `section_touched` first touch of each section per mount (deduped below)
//   - `media_added`     once per add, carrying the batch count
//   - `heartbeat`       at most once per 60s, and only if something changed
//
// There is no pagehide ping: it would need an MSAL bearer token synchronously,
// which acquireTokenSilent cannot provide during page teardown. The heartbeat
// already bounds last_activity_at staleness to ~60s, which is the accepted limit.
//
// Every ping is fire-and-forget — a failure must never block the technician or
// surface an error, matching how the draft system already degrades.

import { useCallback, useEffect, useRef } from 'react';
import API from '../../../sharedModules/utils/axios.utils';

const ACTIVITY_URL = '/home_services/inspection_report_activities';
const HEARTBEAT_MS = 60_000;

export type ActivitySection =
  | 'hvac'
  | 'plumbing'
  | 'electrical'
  | 'water_treatment'
  | 'air_quality'
  | 'customer_information';

type EventType = 'opened' | 'section_touched' | 'media_added' | 'heartbeat';

interface PingPayload {
  home_inspection_job_id: string;
  event_type: EventType;
  section?: ActivitySection;
  media_kind?: 'photo' | 'video';
  count?: number;
}

export function useInspectionReportActivity(jobId: string | undefined) {
  // Sections already pinged during THIS mount. A reload starts a fresh set and will
  // re-ping as each section is touched again — correct, because that is a new open.
  const pingedSections = useRef<Set<ActivitySection>>(new Set());
  // True when a tracked interaction has happened since the last successful ping.
  // An idle open form never heartbeats.
  const dirty = useRef(false);
  // Bumped whenever dirty is set so an in-flight ping's success handler cannot
  // clear a dirtiness that arrived after that request was sent.
  const dirtyGeneration = useRef(0);
  const openedFor = useRef<string | undefined>(undefined);
  const jobIdRef = useRef(jobId);

  useEffect(() => {
    jobIdRef.current = jobId;
  }, [jobId]);

  const markDirty = useCallback(() => {
    dirty.current = true;
    dirtyGeneration.current += 1;
  }, []);

  const ping = useCallback((payload: Omit<PingPayload, 'home_inspection_job_id'>) => {
    const id = jobIdRef.current;
    if (!id) return;

    const generationAtSend = dirtyGeneration.current;
    API.post(ACTIVITY_URL, { home_inspection_job_id: id, ...payload })
      .then(() => {
        if (dirtyGeneration.current === generationAtSend) {
          dirty.current = false;
        }
      })
      .catch(() => {});
  }, []);

  const trackOpen = useCallback(() => {
    const id = jobIdRef.current;
    // Guards against React 18 Strict Mode double-invoking effects on the *same*
    // mount instance (refs persist across that double-invoke). A true unmount /
    // remount resets refs and correctly counts a new open.
    if (!id || openedFor.current === id) return;
    openedFor.current = id;
    ping({ event_type: 'opened' });
  }, [ping]);

  const trackSection = useCallback(
    (section: ActivitySection) => {
      markDirty();
      if (pingedSections.current.has(section)) return;
      pingedSections.current.add(section);
      ping({ event_type: 'section_touched', section });
    },
    [ping, markDirty],
  );

  const trackMedia = useCallback(
    (kind: 'photo' | 'video', count: number) => {
      if (count <= 0) return;
      markDirty();
      ping({ event_type: 'media_added', media_kind: kind, count });
    },
    [ping, markDirty],
  );

  useEffect(() => {
    if (!jobId) return undefined;

    const timer = window.setInterval(() => {
      if (!dirty.current) return;
      ping({ event_type: 'heartbeat' });
    }, HEARTBEAT_MS);

    return () => window.clearInterval(timer);
  }, [jobId, ping]);

  return { trackOpen, trackSection, trackMedia };
}
